<template>
  <div v-if="hasAccess" class="space-y-6 pb-10">
    <page-content>
      <section class="space-y-6">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <dashboard-date-filters
            class="xl:flex-1"
            :quick-ranges="quickRanges"
            :active-range-id="activeQuickRangeId"
            :date-range="dateRange"
            :max-date="maxDate"
            @select-range="applyQuickRange"
            @show-full="showFull"
            @update:date-range="handleDateRangeUpdate"
          />

          <div class="tracking-filters inline-flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 bg-white px-4 py-3 shadow-sm xl:min-w-[360px]">
            <div class="tracking-filter-field tracking-filter-field--wide">
              <t-rich-select
                :key="domainSelectKey"
                class="tracking-filter-control"
                multiple
                :close-on-select="false"
                :options="domainOptions"
                value-attribute="uuid"
                text-attribute="label"
                placeholder="Tous les domaines"
                v-model="selectedDomainUuids"
              >
                <template slot="label" slot-scope="{ options, className }">
                  <span :class="className">
                    {{ domainSelectionLabel }}
                  </span>
                </template>
              </t-rich-select>
            </div>
          </div>
        </div>

        <div class="tracking-overview-grid">
          <section class="tracking-space bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="tracking-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Connexions</h2>
                <button
                  type="button"
                  class="tracking-info-button"
                  title="Suivi mois par mois"
                  aria-label="Ouvrir le suivi mois par mois"
                  @click="openMonthlyTracking"
                >
                  +
                </button>
              </div>
              <div class="tracking-period-stats">
                <article class="tracking-period-stat">
                  <span>All période</span>
                  <strong>{{ formatNumber(summaryTotals.all) }}</strong>
                </article>
                <article class="tracking-period-stat">
                  <span>Unique période</span>
                  <strong>{{ formatNumber(summaryTotals.unique) }}</strong>
                </article>
              </div>
            </header>

            <div
              class="tracking-daily-layout"
              :class="{ 'tracking-daily-layout--chart-only': isSingleDomainFilter }"
            >
              <div class="tracking-chart-panel">
                <chart-connection-trend
                  v-if="hasDailySeries"
                  class="h-full w-full"
                  :chart-data="dailySeriesChartData"
                />
                <div v-else class="flex h-full items-center justify-center text-sm text-gray-600">
                  Aucune connexion sur cette période.
                </div>
              </div>

              <article v-if="!isSingleDomainFilter" class="tracking-active-domains-card">
                <div class="tracking-active-domain-meter">
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle class="tracking-active-domain-bg" cx="60" cy="60" r="48" />
                    <circle
                      class="tracking-active-domain-progress"
                      cx="60"
                      cy="60"
                      r="48"
                      :stroke-dasharray="activeDomainCircumference"
                      :stroke-dashoffset="activeDomainStrokeOffset"
                    />
                  </svg>
                  <span>{{ activeDomainPercentLabel }}</span>
                </div>
                <p class="tracking-active-domain-detail">{{ domainsWithConnectionsLabel }}</p>
                <button
                  type="button"
                  class="tracking-link-button"
                  @click="openDomainsWithoutConnections"
                >
                  Voir les domaines inactifs
                </button>
              </article>
            </div>
          </section>

          <section class="tracking-space bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="tracking-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Intensité</h2>
                <span class="tracking-info-button tracking-info-button--static" title="Connexions all divisées par connexions uniques">i</span>
              </div>
              <article class="tracking-period-stat">
                <span>All / uniques</span>
                <strong>{{ intensityLabel }}</strong>
              </article>
            </header>

            <div class="tracking-intensity-layout">
              <div class="tracking-chart-panel">
                <chart-connection-domain-bar
                  v-if="hasDomainSeries"
                  class="h-full w-full"
                  :chart-data="domainSeriesChartData"
                />
                <div v-else class="flex h-full items-center justify-center text-sm text-gray-600">
                  Aucun domaine à comparer sur cette période.
                </div>
              </div>
            </div>
          </section>
        </div>

        <div class="bg-white rounded-2xl shadow-sm border border-blue-200">
          <div class="border-b border-gray-200 px-5 py-4">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">
                  Détail client
                </h2>
                <p class="mt-1 text-sm text-gray-600">
                  Détail des connexions des administrateurs et observateurs sur l'interface back-office.
                </p>
              </div>
              <p v-if="isSummaryLoading && !isMonthlyTrackingOpen" class="text-sm text-gray-600">Mise à jour des indicateurs…</p>
            </div>
          </div>
          <div class="px-2 py-4">
            <m-v-p-table
              v-if="gridOptions"
              :options="gridOptions"
              :grid-data="gridData"
              status-bar-type="total"
              @gridReady="onGridReady"
            />
          </div>
        </div>

        <div
          v-if="isMonthlyTrackingOpen"
          class="tracking-modal-overlay"
          @click.self="closeMonthlyTracking"
        >
          <div class="tracking-modal-card tracking-modal-card--wide">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Suivi mois par mois</h3>
                <p class="tracking-modal-subtitle">
                  Connexions et domaines actifs sur l'année sélectionnée.
                </p>
              </div>
              <div class="tracking-modal-domain-filter">
                <label>Client</label>
                <t-rich-select
                  :key="`${domainSelectKey}::monthly`"
                  class="tracking-filter-control"
                  multiple
                  :close-on-select="false"
                  :options="domainOptions"
                  value-attribute="uuid"
                  text-attribute="label"
                  placeholder="Tous les domaines"
                  v-model="selectedDomainUuids"
                >
                  <template slot="label" slot-scope="{ options, className }">
                    <span :class="className">
                      {{ domainSelectionLabel }}
                    </span>
                  </template>
                </t-rich-select>
              </div>
              <button
                type="button"
                class="tracking-modal-close"
                @click="closeMonthlyTracking"
              >
                ×
              </button>
            </header>

            <div class="tracking-modal-table-wrapper tracking-monthly-table-wrapper">
              <table class="tracking-modal-table tracking-monthly-table">
                <thead>
                  <tr>
                    <th>
                      <select v-model.number="selectedTrackingYear" class="tracking-year-select">
                        <option v-for="year in trackingYearOptions" :key="year" :value="year">
                          {{ year }}
                        </option>
                      </select>
                    </th>
                    <th v-for="month in monthlyTrackingRows" :key="month.key" class="is-right">
                      {{ month.label }}
                    </th>
                  </tr>
                </thead>
                <tbody v-if="!isMonthlyTrackingLoading">
                  <tr>
                    <th>Connexions all</th>
                    <td v-for="month in monthlyTrackingRows" :key="`all-${month.key}`" class="is-right">
                      {{ formatNumber(month.all) }}
                    </td>
                  </tr>
                  <tr>
                    <th>Connexions uniques</th>
                    <td v-for="month in monthlyTrackingRows" :key="`unique-${month.key}`" class="is-right">
                      {{ formatNumber(month.unique) }}
                    </td>
                  </tr>
                  <tr v-if="!isSingleDomainFilter">
                    <th>% de domaine actif</th>
                    <td v-for="month in monthlyTrackingRows" :key="`active-${month.key}`" class="is-right">
                      {{ formatPercent(month.activeDomainRate) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="isMonthlyTrackingLoading" class="tracking-monthly-loader" aria-live="polite">
                <span class="tracking-monthly-spinner" />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="isInactiveDomainsModalOpen"
          class="tracking-modal-overlay"
          @click.self="closeDomainsWithoutConnectionsModal"
        >
          <div class="tracking-modal-card">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Domaines sans connexion</h3>
                <p class="tracking-modal-subtitle">
                  Aucun login administrateur/observateur sur la période sélectionnée.
                </p>
              </div>
              <button
                type="button"
                class="tracking-modal-close"
                @click="closeDomainsWithoutConnectionsModal"
              >
                ×
              </button>
            </header>

            <div class="tracking-modal-table-wrapper">
              <table class="tracking-modal-table">
                <thead>
                  <tr>
                    <th>Domaine</th>
                    <th class="is-right">Admins</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isSummaryLoading">
                    <td colspan="2">Chargement…</td>
                  </tr>
                  <tr v-else-if="domainsWithoutConnectionsRows.length === 0">
                    <td colspan="2">Tous les domaines filtrés ont eu au moins une connexion.</td>
                  </tr>
                  <tr v-for="domain in domainsWithoutConnectionsRows" :key="domain.domain_uuid">
                    <td>{{ domain.domain_label }}</td>
                    <td class="is-right">{{ formatNumber(domain.admins) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </page-content>
  </div>
  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-600">
      Vous n'avez pas accès à cette section.
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { http } from '@/services/api.service'
import { format, subDays, subWeeks, subMonths, parseISO } from 'date-fns'
import fr from 'date-fns/locale/fr'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import DashboardDateFilters from '@/components/dashboard/DashboardDateFilters.vue'
import ChartConnectionTrend from '@/components/charts/ChartConnectionTrend.vue'
import ChartConnectionDomainBar from '@/components/charts/ChartConnectionDomainBar.vue'
import { DASHBOARD_QUICK_RANGES } from './state'
import { getFullTrackingDateRange } from './domainPeriod'
import { fetchTrackingDomains } from './trackingDomains'
import { formatTrackingDomainSelectionLabel, getTrackingQueryState, normalizeTrackingDomainUuids, syncTrackingQueryFromVm } from './trackingQuery'

const DATE_FORMAT = 'yyyy-MM-dd'

function toIsoDate (date) {
  return format(date, DATE_FORMAT)
}

function safeParseIsoDate (value) {
  if (!value || typeof value !== 'string') {
    return null
  }

  try {
    const parsed = parseISO(value)
    if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
      return parsed
    }
  } catch (error) {
    //
  }

  return null
}

function createDefaultSummary () {
  return {
    totals: {
      all: 0,
      unique: 0,
      repeated: 0,
      intensity: 0,
      totalAdmins: 0,
      domainsWithConnections: 0,
      openDomains: 0,
      withoutConnections: 0
    },
    dailySeries: [],
    monthlySeries: [],
    domains: [],
    domainsWithoutConnections: []
  }
}

export default {
  name: 'app-dashboard-connection-stats',
  components: {
    PageContent,
    MVPTable,
    DashboardDateFilters,
    ChartConnectionTrend,
    ChartConnectionDomainBar
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      const hasAccess = await vm.ensureAccess()
      if (!hasAccess) {
        vm.$router.replace('/app/dashboard')
      }
    })
  },
  data () {
    const today = new Date()
    const defaultEnd = toIsoDate(today)
    const defaultStart = toIsoDate(subDays(today, 29))
    const currentYear = today.getFullYear()
    const trackingState = getTrackingQueryState(this.$route.query, {
      activeQuickRangeId: 'month-1',
      dateRange: [defaultStart, defaultEnd],
      selectedDomainUuids: []
    })

    return {
      quickRanges: DASHBOARD_QUICK_RANGES.map(range => ({ ...range })),
      activeQuickRangeId: trackingState.activeQuickRangeId,
      dateRange: trackingState.dateRange,
      maxDate: defaultEnd,
      domainOptions: [],
      selectedDomainUuids: trackingState.selectedDomainUuids,
      gridOptions: null,
      gridData: null,
      gridApi: null,
      totalRowCount: 0,
      summary: createDefaultSummary(),
      monthlySeries: [],
      isSummaryLoading: false,
      isMonthlyTrackingLoading: false,
      isInactiveDomainsModalOpen: false,
      isMonthlyTrackingOpen: false,
      selectedTrackingYear: currentYear,
      summaryRequestId: 0,
      monthlyRequestId: 0,
      activeSummaryRequestKey: null,
      activeMonthlyRequestKey: null,
      debouncedRefreshTrackingData: null,
      debouncedLoadMonthlyTracking: null,
      isInitializingTracking: true
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.includes('admin') || this.roles.includes('community-manager')
    },
    isSingleDomainFilter () {
      return normalizeTrackingDomainUuids(this.selectedDomainUuids).length === 1
    },
    domainSelectionLabel () {
      return formatTrackingDomainSelectionLabel(this.selectedDomainUuids, this.domainOptions)
    },
    domainSelectKey () {
      return `${this.$route.path}::${this.domainOptions.length}`
    },
    summaryTotals () {
      return this.summary?.totals || createDefaultSummary().totals
    },
    normalizedDailySeries () {
      const rows = Array.isArray(this.summary?.dailySeries) ? this.summary.dailySeries : []
      return rows.map(row => ({
        date: row?.date || '',
        all: Number(row?.all || 0)
      }))
    },
    normalizedDomainSeries () {
      const rows = Array.isArray(this.summary?.domains) ? this.summary.domains : []
      return rows.map(row => ({
        domainLabel: row?.domain_label || 'Domaine',
        all: Number(row?.all || 0),
        unique: Number(row?.unique || 0)
      }))
    },
    hasDailySeries () {
      return this.normalizedDailySeries.some(row => row.all > 0)
    },
    hasDomainSeries () {
      return this.normalizedDomainSeries.some(row => row.all > 0 || row.unique > 0)
    },
    intensityLabel () {
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(this.summaryTotals.intensity || 0))}x`
    },
    uniqueConnectionsLabel () {
      return `${this.formatNumber(this.summaryTotals.unique)} connexions uniques / ${this.formatNumber(this.summaryTotals.totalAdmins)} admins`
    },
    domainsWithConnectionsLabel () {
      return `${this.formatNumber(this.summaryTotals.domainsWithConnections)} / ${this.formatNumber(this.summaryTotals.openDomains)} domaines actifs`
    },
    activeDomainRate () {
      const total = Number(this.summaryTotals.openDomains || 0)
      if (total <= 0) {
        return 0
      }

      return Math.min(100, Math.max(0, (Number(this.summaryTotals.domainsWithConnections || 0) / total) * 100))
    },
    activeDomainPercentLabel () {
      return this.formatPercent(this.activeDomainRate)
    },
    activeDomainCircumference () {
      return 2 * Math.PI * 48
    },
    activeDomainStrokeOffset () {
      return this.activeDomainCircumference * (1 - (this.activeDomainRate / 100))
    },
    trackingYearOptions () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2020; year--) {
        years.push(year)
      }
      return years
    },
    monthlyTrackingRows () {
      const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
      const rows = Array.isArray(this.monthlySeries) ? this.monthlySeries : []
      const rowsByMonth = rows.reduce((acc, row) => {
        acc[Number(row?.month || 0)] = row
        return acc
      }, {})

      return monthLabels.map((label, index) => {
        const month = index + 1
        const row = rowsByMonth[month] || {}

        return {
          key: month,
          label,
          all: Number(row?.all || 0),
          unique: Number(row?.unique || 0),
          activeDomainRate: Number(row?.activeDomainRate || 0)
        }
      })
    },
    domainsWithoutConnectionsRows () {
      const rows = Array.isArray(this.summary?.domainsWithoutConnections) ? this.summary.domainsWithoutConnections : []
      return rows
        .map(row => ({
          domain_uuid: row?.domain_uuid || '',
          domain_label: row?.domain_label || 'Domaine',
          admins: Number(row?.admins || 0)
        }))
        .sort((a, b) => String(a.domain_label).localeCompare(String(b.domain_label), 'fr', { sensitivity: 'base' }))
    },
    dailySeriesChartData () {
      return {
        labels: this.normalizedDailySeries.map(item => this.formatDateLabel(item.date)),
        datasets: [
          {
            label: 'Connexions ALL',
            data: this.normalizedDailySeries.map(item => item.all),
            borderColor: '#2563EB',
            backgroundColor: 'rgba(37, 99, 235, 0.12)',
            borderWidth: this.normalizedDailySeries.length > 31 ? 0.75 : 1,
            pointBackgroundColor: '#2563EB',
            pointRadius: this.normalizedDailySeries.length > 31 ? 0 : (this.normalizedDailySeries.length > 14 ? 1 : 1.5),
            pointHoverRadius: 2,
            pointHitRadius: 8,
            fill: false
          }
        ]
      }
    },
    domainSeriesChartData () {
      const topDomains = this.normalizedDomainSeries.slice(0, 10)
      return {
        labels: topDomains.map(item => this.formatDomainChartLabel(item.domainLabel)),
        datasets: [
          {
            label: 'Connexions ALL',
            data: topDomains.map(item => item.all),
            backgroundColor: '#2563EB'
          },
          {
            label: 'Connexions uniques',
            data: topDomains.map(item => item.unique),
            backgroundColor: '#0F766E'
          }
        ]
      }
    }
  },
  watch: {
    selectedDomainUuids: {
      handler () {
        if (this.activeQuickRangeId === 'full') {
          this.setFullDateRange()
        }
        syncTrackingQueryFromVm(this)
        if (this.isInitializingTracking) {
          return
        }
        this.scheduleTrackingRefresh()
        if (this.isMonthlyTrackingOpen) {
          this.scheduleMonthlyTrackingReload()
        }
      },
      deep: true
    },
    selectedTrackingYear () {
      if (!this.isInitializingTracking && this.isMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    }
  },
  async created () {
    this.initializeGridOptions()
    this.debouncedRefreshTrackingData = debounce(this.refreshTrackingData, 400)
    this.debouncedLoadMonthlyTracking = debounce(this.loadMonthlyTracking, 400)

    const hasAccess = await this.ensureAccess()
    if (!hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    const waitForDomains = this.activeQuickRangeId === 'full'
    const domainsPromise = this.fetchDomains()
    if (waitForDomains) {
      await domainsPromise
      this.setFullDateRange()
    }
    syncTrackingQueryFromVm(this)
    this.isInitializingTracking = false
    this.initializeGridDataSource()
    this.loadSummary()
    if (this.isMonthlyTrackingOpen) {
      this.loadMonthlyTracking()
    }
    if (!waitForDomains) {
      await domainsPromise
    }
  },
  beforeDestroy () {
    if (this.debouncedRefreshTrackingData) {
      this.debouncedRefreshTrackingData.cancel()
    }
    if (this.debouncedLoadMonthlyTracking) {
      this.debouncedLoadMonthlyTracking.cancel()
    }
    this.summaryRequestId += 1
    this.monthlyRequestId += 1
  },
  methods: {
    async ensureAccess () {
      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        try {
          await this.$store.dispatch('FETCH_ROLES')
        } catch (error) {
          return false
        }
      }

      return this.hasAccess
    },
    initializeGridOptions () {
      this.gridOptions = {
        rowModelType: 'serverSide',
        suppressServerSideInfiniteScroll: false,
        cacheBlockSize: 100,
        maxBlocksInCache: 8,
        context: {
          componentParent: this
        },
        defaultColDef: {
          resizable: true,
          sortable: true,
          floatingFilter: false
        },
        columnDefs: [
          {
            headerName: 'Date de connexion',
            field: 'logged_at',
            colId: 'logged_at',
            sortable: true,
            valueFormatter: params => this.formatDateTime(params.value),
            sort: 'desc',
            minWidth: 180
          },
          {
            headerName: 'Domaine',
            field: 'domain_label',
            colId: 'domain_label',
            sortable: true,
            minWidth: 180
          }
        ]
      }
    },
    async fetchDomains () {
      try {
        const domains = await fetchTrackingDomains()
        this.domainOptions = domains

        const allowedUuids = new Set(domains.map(domain => domain.uuid))
        const currentSelectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
        const nextSelectedDomainUuids = currentSelectedDomainUuids
          .filter(uuid => allowedUuids.has(uuid))

        if (nextSelectedDomainUuids.join('|') !== currentSelectedDomainUuids.join('|')) {
          this.selectedDomainUuids = nextSelectedDomainUuids
        }
      } catch (error) {
        this.domainOptions = []
      }
    },
    scheduleTrackingRefresh () {
      if (this.debouncedRefreshTrackingData) {
        this.debouncedRefreshTrackingData()
        return
      }
      this.refreshTrackingData()
    },
    scheduleMonthlyTrackingReload () {
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking()
        return
      }
      this.loadMonthlyTracking()
    },
    refreshTrackingData () {
      if (this.debouncedRefreshTrackingData) {
        this.debouncedRefreshTrackingData.cancel()
      }
      this.refreshGrid()
      this.loadSummary()
    },
    applyQuickRange (range) {
      if (!range) return

      this.activeQuickRangeId = range.id
      const today = new Date()
      let start = null

      if (range.type === 'week') {
        start = subWeeks(today, range.amount)
      } else if (range.type === 'month') {
        start = subMonths(today, range.amount)
      } else if (range.type === 'day') {
        start = subDays(today, range.amount - 1)
      }

      if (!start) {
        start = subDays(today, 29)
      }

      this.dateRange = [toIsoDate(start), toIsoDate(today)]
      syncTrackingQueryFromVm(this)
      this.refreshTrackingData()
    },
    showFull () {
      this.activeQuickRangeId = 'full'
      this.setFullDateRange()
      syncTrackingQueryFromVm(this)
      this.refreshTrackingData()
    },
    setFullDateRange () {
      this.dateRange = getFullTrackingDateRange(this.domainOptions, this.selectedDomainUuids)
      this.maxDate = this.dateRange[1]
    },
    handleDateRangeUpdate (range) {
      if (!Array.isArray(range) || range.length !== 2) {
        this.dateRange = null
      } else {
        this.dateRange = [...range]
      }
      this.activeQuickRangeId = null
      syncTrackingQueryFromVm(this)
      this.scheduleTrackingRefresh()
    },
    onGridReady (params) {
      this.gridApi = params.api
      if (!this.isInitializingTracking) {
        this.initializeGridDataSource()
      }
    },
    initializeGridDataSource () {
      if (!this.gridApi) {
        return
      }
      const datasource = this.createDataSource()
      this.gridApi.setServerSideDatasource(datasource)
    },
    createDataSource () {
      return {
        getRows: (params) => {
          const queryParams = this.buildQueryParams(params.request)

          http
            .get('/dashboard/login-stats', { params: queryParams })
            .then(({ data }) => {
              const rows = Array.isArray(data?.rows) ? data.rows : []
              const total = Number.isFinite(data?.totalCount) ? data.totalCount : rows.length
              this.totalRowCount = total
              params.api.dispatchEvent({ type: 'totalRowCountUpdated' })
              if (typeof params.success === 'function') {
                params.success({ rowData: rows, rowCount: total })
              } else if (typeof params.successCallback === 'function') {
                params.successCallback(rows, total)
              }
            })
            .catch(() => {
              if (typeof params.fail === 'function') {
                params.fail()
              } else if (typeof params.failCallback === 'function') {
                params.failCallback()
              }
            })
        }
      }
    },
    loadSummary () {
      if (this.isInitializingTracking) {
        return
      }
      const params = {
        ...this.buildSummaryQueryParams(),
        summaryOnly: 1
      }
      const requestKey = JSON.stringify(params)
      if (this.activeSummaryRequestKey === requestKey) {
        return
      }

      const requestId = this.summaryRequestId + 1
      this.summaryRequestId = requestId
      this.activeSummaryRequestKey = requestKey
      this.isSummaryLoading = true

      http
        .get('/dashboard/login-stats', { params })
        .then(({ data }) => {
          if (requestId !== this.summaryRequestId) {
            return
          }

          const summary = data?.summary || {}
          const totals = summary?.totals || {}
          const dailySeries = Array.isArray(summary?.dailySeries) ? summary.dailySeries : []
          const domains = Array.isArray(summary?.domains) ? summary.domains : []

          this.summary = {
            totals: {
              all: Number(totals?.all || 0),
              unique: Number(totals?.unique || 0),
              repeated: Number(totals?.repeated || 0),
              intensity: Number(totals?.intensity || 0),
              totalAdmins: Number(totals?.totalAdmins || 0),
              domainsWithConnections: Number(totals?.domainsWithConnections || 0),
              openDomains: Number(totals?.openDomains || 0),
              withoutConnections: Number(totals?.withoutConnections || 0)
            },
            dailySeries,
            domains,
            domainsWithoutConnections: Array.isArray(summary?.domainsWithoutConnections) ? summary.domainsWithoutConnections : []
          }
        })
        .catch(() => {
          if (requestId !== this.summaryRequestId) {
            return
          }

          this.summary = createDefaultSummary()
        })
        .finally(() => {
          if (requestId === this.summaryRequestId) {
            this.isSummaryLoading = false
            this.activeSummaryRequestKey = null
          }
        })
    },
    loadMonthlyTracking () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }

      const params = {
        ...this.buildSummaryQueryParams(),
        summaryOnly: 1,
        includeMonthly: 1
      }
      const requestKey = JSON.stringify(params)
      if (this.activeMonthlyRequestKey === requestKey) {
        return
      }

      const requestId = this.monthlyRequestId + 1
      this.monthlyRequestId = requestId
      this.activeMonthlyRequestKey = requestKey
      this.isMonthlyTrackingLoading = true

      http
        .get('/dashboard/login-stats', { params })
        .then(({ data }) => {
          if (requestId !== this.monthlyRequestId) {
            return
          }
          const monthlySeries = Array.isArray(data?.summary?.monthlySeries) ? data.summary.monthlySeries : []
          this.monthlySeries = monthlySeries
        })
        .catch(() => {
          if (requestId === this.monthlyRequestId) {
            this.monthlySeries = []
          }
        })
        .finally(() => {
          if (requestId === this.monthlyRequestId) {
            this.isMonthlyTrackingLoading = false
            this.activeMonthlyRequestKey = null
          }
        })
    },
    buildQueryParams (request) {
      const params = {
        startRow: request.startRow,
        endRow: request.endRow,
        sortModel: JSON.stringify(request.sortModel || [])
      }

      const [dateStart, dateEnd] = this.normalizeDateRange()
      if (dateStart && dateEnd) {
        params.dateStart = dateStart
        params.dateEnd = dateEnd
      }

      const selectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
      if (selectedDomainUuids.length) {
        params.domains = selectedDomainUuids
      }

      return params
    },
    buildSummaryQueryParams () {
      const params = {}
      const [dateStart, dateEnd] = this.normalizeDateRange()

      if (dateStart && dateEnd) {
        params.dateStart = dateStart
        params.dateEnd = dateEnd
      }

      const selectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
      if (selectedDomainUuids.length) {
        params.domains = selectedDomainUuids
      }

      params.trackingYear = this.selectedTrackingYear

      return params
    },
    normalizeDateRange () {
      if (!Array.isArray(this.dateRange) || this.dateRange.length !== 2) {
        return [null, null]
      }
      const [start, end] = this.dateRange
      return [start || null, end || null]
    },
    refreshGrid () {
      if (this.gridApi) {
        this.gridApi.refreshServerSideStore({ purge: true })
      }
    },
    formatDateTime (value) {
      if (!value) {
        return ''
      }

      const parsed = safeParseIsoDate(value)
      if (!parsed) {
        return value
      }

      return format(parsed, 'dd/MM/yyyy HH:mm', { locale: fr })
    },
    formatDateLabel (value) {
      const parsed = safeParseIsoDate(value)
      if (!parsed) {
        return value
      }

      return format(parsed, 'dd/MM', { locale: fr })
    },
    formatDomainChartLabel (value) {
      const text = String(value || '').trim()
      if (text.length <= 20) {
        return text
      }

      return `${text.slice(0, 18)}…`
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    },
    formatPercent (value) {
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }).format(Number(value || 0))} %`
    },
    openDomainsWithoutConnections () {
      this.isInactiveDomainsModalOpen = true
    },
    closeDomainsWithoutConnectionsModal () {
      this.isInactiveDomainsModalOpen = false
    },
    openMonthlyTracking () {
      this.isMonthlyTrackingOpen = true
      this.monthlySeries = []
      if (!this.isInitializingTracking) {
        this.loadMonthlyTracking()
      }
    },
    closeMonthlyTracking () {
      this.isMonthlyTrackingOpen = false
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }
    }
  }
}
</script>

<style scoped>
.tracking-space {
  overflow: hidden;
}

.tracking-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.5rem;
}

.tracking-space-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.tracking-info-button--static {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.tracking-daily-layout,
.tracking-intensity-layout {
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
}

.tracking-daily-layout {
  grid-template-columns: minmax(0, 1fr) minmax(150px, 190px);
  align-items: center;
}

.tracking-daily-layout--chart-only {
  grid-template-columns: minmax(0, 1fr);
}

.tracking-intensity-layout {
  grid-template-columns: minmax(0, 1fr);
}

.tracking-chart-panel {
  height: 20rem;
  min-width: 0;
  overflow: hidden;
}

.tracking-daily-stats {
  display: grid;
  gap: 1rem;
}

.tracking-stat-card,
.tracking-intensity-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid #bfdbfe;
  border-radius: 0.5rem;
  background: #eff6ff;
  padding: 1rem;
}

.tracking-stat-label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2563eb;
}

.tracking-stat-value,
.tracking-intensity-value {
  margin: 0.6rem 0 0;
  color: #111827;
  font-weight: 700;
}

.tracking-stat-value {
  font-size: 1.875rem;
}

.tracking-intensity-value {
  font-size: 2.25rem;
}

.tracking-active-domains-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  text-align: center;
}

.tracking-active-domain-meter {
  position: relative;
  width: 8rem;
  height: 8rem;
  margin-top: 0.75rem;
}

.tracking-active-domain-meter svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.tracking-active-domain-bg,
.tracking-active-domain-progress {
  fill: none;
  stroke-width: 12;
}

.tracking-active-domain-bg {
  stroke: #e5e7eb;
}

.tracking-active-domain-progress {
  stroke: #2563eb;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.25s ease;
}

.tracking-active-domain-meter span {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111827;
  font-size: 1.35rem;
  font-weight: 700;
}

.tracking-active-domain-detail {
  margin: 0.5rem 0 0;
  color: #4b5563;
  font-size: 0.875rem;
}

.tracking-link-button {
  margin-top: 0.75rem;
  color: #2563eb;
  font-size: 0.875rem;
  font-weight: 600;
}

.tracking-link-button:hover {
  color: #1d4ed8;
}

.tracking-intensity-list {
  margin: 1rem 0 0;
}

.tracking-intensity-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 0;
  border-top: 1px solid #e5e7eb;
}

.tracking-intensity-list dt {
  color: #4b5563;
  font-size: 0.875rem;
}

.tracking-intensity-list dd {
  margin: 0;
  color: #111827;
  font-weight: 700;
}

.tracking-modal-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

@media (max-width: 1024px) {
  .tracking-overview-grid,
  .tracking-daily-layout,
  .tracking-intensity-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .tracking-space-header,
  .tracking-modal-header {
    flex-direction: column;
  }

  .tracking-period-stats {
    justify-content: flex-start;
    width: 100%;
  }

  .tracking-period-stat {
    flex: 1 1 8rem;
  }

  .tracking-info-button {
    align-self: flex-start;
  }

  .tracking-modal-domain-filter {
    width: 100%;
    margin-left: 0;
  }

  .tracking-chart-panel {
    height: 18rem;
  }
}
</style>
